import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import * as d3 from 'd3';
import { vendorLogoUrl } from '../lib/logoUtils';

const CATEGORY_ANGLES = {
  'core-ops': -90,
  'rev-dist': -54,
  'sales-events': -18,
  'marketing': 18,
  'guest-exp': 54,
  'fb-tech': 90,
  'finance-hr': 126,
  'ancillary': 162,
  'it-infra': 198,
  'collab': 234,
};

const FONT_PLATFORM = 'IBM Plex Sans, sans-serif';
const FONT_VENDOR = 'IBM Plex Mono, monospace';


const GraphCanvas = forwardRef(({
  data,
  focusedCategory,
  selectedNode,
  bulkSelectedIds,
  onNodeSelect,
  onCanvasClick,
  searchQuery,
  showUsedOnly,
  hideUnconnected,
  selectedProperty,
  editMode,
  editSourceNode,
  compareMode,
  compareData,
  onLayoutChange,
}, ref) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const simulationRef = useRef(null);
  const zoomRef = useRef(null);
  const tooltipRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [layoutMode, setLayoutMode] = useState(() => {
    return localStorage.getItem('lodgiflow_layout_mode') || 'category';
  });
  const layoutModeRef = useRef(localStorage.getItem('lodgiflow_layout_mode') || 'category');
  const layoutFnsRef = useRef({});

  // Persist layout mode to localStorage + notify parent (skip the very first render so we
  // don't echo the freshly-loaded stored value back to the backend on mount)
  const layoutModeInitRef = useRef(true);
  useEffect(() => {
    localStorage.setItem('lodgiflow_layout_mode', layoutMode);
    layoutModeRef.current = layoutMode;
    if (layoutModeInitRef.current) {
      layoutModeInitRef.current = false;
      return;
    }
    if (typeof onLayoutChange === 'function') onLayoutChange(layoutMode);
  }, [layoutMode, onLayoutChange]);

  // Node radius function — accessible from both useEffect and useImperativeHandle
  const nodeRadiusFn = (d) => d._radius || 24;

  const [labelSwap, setLabelSwap] = useState(false);
  const labelSwapRef = useRef(false);
  const [lineOpacity, setLineOpacity] = useState(0.8);
  const [lineThickness, setLineThickness] = useState(2);
  const [logoOpacity, setLogoOpacity] = useState(() => parseFloat(localStorage.getItem('lodgiflow_logo_opacity') || '0.3'));
  const [logoStyle, setLogoStyle] = useState(() => localStorage.getItem('lodgiflow_logo_style') || 'watermark');
  const logoOpacityRef = useRef(parseFloat(localStorage.getItem('lodgiflow_logo_opacity') || '0.3'));
  const logoStyleRef = useRef(localStorage.getItem('lodgiflow_logo_style') || 'watermark');
  const userHasZoomedRef = useRef(false);
  const [mapNeedsRefresh, setMapNeedsRefresh] = useState(false);
  const nodesDataRef = useRef([]);
  const linksDataRef = useRef([]);
  const onNodeSelectRef = useRef(onNodeSelect);
  const onCanvasClickRef = useRef(onCanvasClick);

  // Keep refs in sync with latest props
  useEffect(() => {
    onNodeSelectRef.current = onNodeSelect;
  }, [onNodeSelect]);

  useEffect(() => {
    onCanvasClickRef.current = onCanvasClick;
  }, [onCanvasClick]);

  const fitToView = (force = false) => {
    if (!svgRef.current || !zoomRef.current) return;
    // Don't auto-fit if user has manually zoomed/panned (unless forced by layout change)
    if (!force && userHasZoomedRef.current) {
      setMapNeedsRefresh(true);
      return;
    }
    const svg = d3.select(svgRef.current);
    const nodesGroup = svg.select('.nodes');
    const bounds = nodesGroup.node()?.getBBox();
    if (!bounds || bounds.width === 0) return;
    const { width, height } = dimensions;
    const padding = 60;
    const scale = Math.min(
      (width - padding * 2) / bounds.width,
      (height - padding * 2) / bounds.height
    );
    const finalScale = Math.max(0.15, Math.min(scale, 2.5));
    const tx = width / 2 - (bounds.x + bounds.width / 2) * finalScale;
    const ty = height / 2 - (bounds.y + bounds.height / 2) * finalScale;
    svg.transition().duration(400)
      .call(zoomRef.current.transform, d3.zoomIdentity.translate(tx, ty).scale(finalScale));
    setMapNeedsRefresh(false);
  };

  useImperativeHandle(ref, () => ({
    fitToView,
    getLineOpacity: () => lineOpacity,
    setLineOpacity: (v) => setLineOpacity(v),
    getLineThickness: () => lineThickness,
    setLineThickness: (v) => setLineThickness(v),
    getLogoOpacity: () => logoOpacity,
    setLogoOpacity: (v) => setLogoOpacity(v),
    getLogoStyle: () => logoStyle,
    setLogoStyle: (v) => setLogoStyle(v),
    resetView: () => {
      if (svgRef.current && zoomRef.current) {
        const svg = d3.select(svgRef.current);
        svg.transition().duration(350)
          .call(zoomRef.current.transform, d3.zoomIdentity);
        setTimeout(fitToView, 400);
      }
    },
    getSvgElement: () => svgRef.current,
    exportPng: async () => {
      if (!svgRef.current) return;
      try {
        const { toPng } = await import('html-to-image');
        const container = svgRef.current.closest('.graph-container');
        if (!container) return;
        const dataUrl = await toPng(container, { quality: 0.95, pixelRatio: 2, backgroundColor: '#F7FAFC', filter: (node) => !(node.classList && node.classList.contains('graph-controls-overlay')) });
        const link = document.createElement('a');
        link.download = `lodgiflow-stack-map-${Date.now()}.png`;
        link.href = dataUrl;
        link.click();
      } catch (e) { console.error('PNG export failed:', e); }
    },
    exportSvg: () => {
      if (!svgRef.current) return;
      const svgClone = svgRef.current.cloneNode(true);
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const blob = new Blob([svgClone.outerHTML], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `lodgiflow-stack-map-${Date.now()}.svg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    },
    toggleLabelSwap: () => {
      const newVal = !labelSwapRef.current;
      labelSwapRef.current = newVal;
      setLabelSwap(newVal);
    },

    // Apply a stored layout mode by name (used to hydrate from saved viz-preferences).
    // Idempotent: no-op if the requested mode already matches the current internal state
    // (avoids visually flapping the layout during startup hydration).
    applyStoredLayout: (mode) => {
      if (!mode || mode === layoutModeRef.current) return;
      const map = {
        'category': 'layoutByCategory',
        'logo-cluster': 'layoutLogoCluster',
        'logo-cluster-soft': 'layoutLogoClusterSoft',
        'pms-hub': 'layoutPmsHub',
        'grid': 'layoutGrid',
        'radial': 'layoutRadial',
        'flowchart': 'layoutFlowChart',
        'orgchart': 'layoutOrgChart',
        'clean1': 'layoutClean1',
        'clean2': 'layoutClean2',
        'swim-cat': 'layoutSwimCat',
        'swim-prop': 'layoutSwimProp',
      };
      const fn = map[mode];
      // Suppress the parent callback so hydrating from the server doesn't echo back a save
      layoutModeInitRef.current = true;
      const handle = (typeof ref === 'object' && ref) ? ref.current : null;
      if (fn && handle && typeof handle[fn] === 'function') handle[fn]();
    },

    // Helper to properly reheat simulation for layout changes
    _reheatSim: () => {
      const sim = simulationRef.current;
      if (!sim) return;
      sim.alphaTarget(0.1).alpha(1).restart();
      setTimeout(() => { sim.alphaTarget(0); }, 2000);
      setTimeout(() => fitToView(true), 2500);
    },

    // Layout: same clustering as default, logo-focused node styling
    layoutLogoCluster: () => {
      if (typeof ref !== 'function' && ref?.current?.layoutByCategory) ref.current.layoutByCategory();
      setLayoutMode('logo-cluster');
      layoutModeRef.current = 'logo-cluster';
    },

    // Layout: soft/floating logo variant — no outer strokes, shaded platform bubble bg,
    // logo floats on top with transparent-white processing on load
    layoutLogoClusterSoft: () => {
      if (typeof ref !== 'function' && ref?.current?.layoutByCategory) ref.current.layoutByCategory();
      setLayoutMode('logo-cluster-soft');
      layoutModeRef.current = 'logo-cluster-soft';
    },

    // Layout: arrange by category groups (default) — integration-weighted
    layoutByCategory: () => {
      setLayoutMode('category');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'category';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const links = linksDataRef.current;
      // Unfix all nodes from Grid/Radial/Flow
      nodes.forEach(d => { d.fx = null; d.fy = null; });
      const { width, height } = dimensions;
      const cx = width / 2, cy = height / 2;

      // Count integrations per node
      const integCount = {};
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        integCount[sid] = (integCount[sid] || 0) + 1;
        integCount[tid] = (integCount[tid] || 0) + 1;
      });
      const maxInteg = Math.max(...Object.values(integCount), 1);

      // Find which categories each node connects to (for bridge detection)
      const nodeCrossCats = {};
      links.forEach(l => {
        const s = typeof l.source === 'object' ? l.source : nodes.find(n => n.id === l.source);
        const t = typeof l.target === 'object' ? l.target : nodes.find(n => n.id === l.target);
        if (!s || !t) return;
        const sCat = s.category_id || '';
        const tCat = t.category_id || '';
        if (sCat !== tCat) {
          if (!nodeCrossCats[s.id]) nodeCrossCats[s.id] = new Set();
          if (!nodeCrossCats[t.id]) nodeCrossCats[t.id] = new Set();
          nodeCrossCats[s.id].add(tCat);
          nodeCrossCats[t.id].add(sCat);
        }
      });

      // Re-establish forces with integration-weighted positioning
      const linkForce = d3.forceLink(links).id(d => d.id).distance(80).strength(0.15);
      sim.force('link', linkForce);
      sim.force('charge', d3.forceManyBody().strength(-280).distanceMax(400));
      sim.force('collision', d3.forceCollide().radius(d => nodeRadiusFn(d) + 28).strength(1).iterations(3));

      // X/Y forces: nodes with more integrations pulled closer to center;
      // bridge nodes (connecting multiple categories) get a blended angle between their connected categories
      sim.force('x', d3.forceX(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const crossCats = nodeCrossCats[d.id];
        const ic = integCount[d.id] || 0;
        // Pull highly-connected nodes toward center
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.28 * (1 - centerPull * 0.5);

        if (crossCats && crossCats.size > 0) {
          // Bridge node: blend position between own category and connected categories
          let sumX = Math.cos(myAngle) * orbitRadius;
          let count = 1;
          crossCats.forEach(catId => {
            const a = (CATEGORY_ANGLES[catId] ?? 0) * Math.PI / 180;
            sumX += Math.cos(a) * orbitRadius * 0.3;
            count += 0.3;
          });
          return cx + sumX / count;
        }
        return cx + Math.cos(myAngle) * orbitRadius;
      }).strength(d => {
        const ic = integCount[d.id] || 0;
        return 0.35 + Math.min(ic / maxInteg, 0.5) * 0.25;
      }));

      sim.force('y', d3.forceY(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const crossCats = nodeCrossCats[d.id];
        const ic = integCount[d.id] || 0;
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.28 * (1 - centerPull * 0.5);

        if (crossCats && crossCats.size > 0) {
          let sumY = Math.sin(myAngle) * orbitRadius;
          let count = 1;
          crossCats.forEach(catId => {
            const a = (CATEGORY_ANGLES[catId] ?? 0) * Math.PI / 180;
            sumY += Math.sin(a) * orbitRadius * 0.3;
            count += 0.3;
          });
          return cy + sumY / count;
        }
        return cy + Math.sin(myAngle) * orbitRadius;
      }).strength(d => {
        const ic = integCount[d.id] || 0;
        return 0.35 + Math.min(ic / maxInteg, 0.5) * 0.25;
      }));

      // Scatter nodes slightly from their fixed positions to give the simulation energy
      nodes.forEach(d => {
        d.vx = (Math.random() - 0.5) * 10;
        d.vy = (Math.random() - 0.5) * 10;
      });

      sim.alpha(1).alphaTarget(0.1).restart();
      setTimeout(() => sim.alphaTarget(0), 2500);
      setTimeout(() => fitToView(true), 3000);
    },
    // Layout: PMS hub — center PMS, ring connections around it
    layoutPmsHub: () => {
      setLayoutMode('pms-hub');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'pms-hub';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      nodes.forEach(d => { d.fx = null; d.fy = null; });
      const links = linksDataRef.current;
      const { width, height } = dimensions;
      const cx = width / 2, cy = height / 2;

      const pmsNode = nodes.find(n => n.id === 'pms');
      if (!pmsNode) return;

      // Find directly connected nodes
      const directIds = new Set();
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        if (sid === 'pms') directIds.add(tid);
        if (tid === 'pms') directIds.add(sid);
      });

      // Find secondary connected (connected to direct, but not PMS)
      const secondaryIds = new Set();
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        if (directIds.has(sid) && tid !== 'pms' && !directIds.has(tid)) secondaryIds.add(tid);
        if (directIds.has(tid) && sid !== 'pms' && !directIds.has(sid)) secondaryIds.add(sid);
      });

      const directArr = [...directIds];
      const secondaryArr = [...secondaryIds];
      const ring1Radius = 220;
      const ring2Radius = 400;

      sim.force('x', d3.forceX(d => {
        if (d.id === 'pms') return cx;
        const directIdx = directArr.indexOf(d.id);
        if (directIdx >= 0) {
          const angle = (directIdx / directArr.length) * Math.PI * 2 - Math.PI / 2;
          return cx + Math.cos(angle) * ring1Radius;
        }
        const secIdx = secondaryArr.indexOf(d.id);
        if (secIdx >= 0) {
          const angle = (secIdx / secondaryArr.length) * Math.PI * 2 - Math.PI / 2;
          return cx + Math.cos(angle) * ring2Radius;
        }
        // Unconnected — push to outer edge
        return cx + (Math.random() - 0.5) * ring2Radius * 2.5;
      }).strength(0.5));

      sim.force('y', d3.forceY(d => {
        if (d.id === 'pms') return cy;
        const directIdx = directArr.indexOf(d.id);
        if (directIdx >= 0) {
          const angle = (directIdx / directArr.length) * Math.PI * 2 - Math.PI / 2;
          return cy + Math.sin(angle) * ring1Radius;
        }
        const secIdx = secondaryArr.indexOf(d.id);
        if (secIdx >= 0) {
          const angle = (secIdx / secondaryArr.length) * Math.PI * 2 - Math.PI / 2;
          return cy + Math.sin(angle) * ring2Radius;
        }
        return cy + (Math.random() - 0.5) * ring2Radius * 2.5;
      }).strength(0.5));

      sim.alphaTarget(0.1).alpha(1).restart(); setTimeout(() => sim.alphaTarget(0), 2000);
      setTimeout(() => fitToView(true), 2500);
    },
    // Layout: Grid — clean structured columns by category
    layoutGrid: () => {
      setLayoutMode('grid');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'grid';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;

      const catGroups = {};
      nodes.forEach(n => {
        if (!catGroups[n.category_id]) catGroups[n.category_id] = [];
        catGroups[n.category_id].push(n);
      });
      const catKeys = Object.keys(catGroups);

      // Compute dynamic spacing from actual node sizes
      const maxNodeW = Math.max(...nodes.map(n => (n._radius || 24) * 2.5), 60);
      const maxNodeH = Math.max(...nodes.map(n => (n._radius || 24) * 1.7), 40);
      const colW = maxNodeW + 24;
      const rowH = maxNodeH + 12;
      const startX = 80;
      const startY = 40;

      nodes.forEach(d => {
        const catIdx = catKeys.indexOf(d.category_id);
        const catNodes = catGroups[d.category_id] || [];
        const idx = catNodes.indexOf(d);
        d.fx = startX + catIdx * colW;
        d.fy = startY + idx * rowH;
      });
      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart();
      sim.alpha(0.01).restart();
      setTimeout(() => fitToView(true), 300);
    },
    // Layout: Radial — categories as wedges from center
    layoutRadial: () => {
      setLayoutMode('radial');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'radial';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const { width, height } = dimensions;
      const cx = width / 2, cy = height / 2;

      const catGroups = {};
      nodes.forEach(n => {
        if (!catGroups[n.category_id]) catGroups[n.category_id] = [];
        catGroups[n.category_id].push(n);
      });
      const catKeys = Object.keys(catGroups);

      // Direct positioning in wedges — dynamic radius step
      const maxNodeR = Math.max(...nodes.map(n => n._radius || 24), 20);
      const radStep = maxNodeR * 2.2;
      nodes.forEach(d => {
        const catIdx = catKeys.indexOf(d.category_id);
        const catNodes = catGroups[d.category_id] || [];
        const nodeIdx = catNodes.indexOf(d);
        const wedgeAngle = (Math.PI * 2) / catKeys.length;
        const startAngle = catIdx * wedgeAngle - Math.PI / 2;
        const angleSpread = wedgeAngle * 0.7;
        const nodeAngle = startAngle + (angleSpread * (nodeIdx / Math.max(catNodes.length - 1, 1))) + (wedgeAngle * 0.15);
        const radius = 140 + nodeIdx * radStep;
        d.fx = cx + Math.cos(nodeAngle) * radius;
        d.fy = cy + Math.sin(nodeAngle) * radius;
      });
      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart();
      sim.alpha(0.01).restart();
      setTimeout(() => fitToView(true), 300);
    },
    // Layout: Flow Chart — rectangular blocks by category
    layoutFlowChart: () => {
      setLayoutMode('flowchart');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'flowchart';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;

      const catGroups = {};
      nodes.forEach(n => {
        if (!catGroups[n.category_id]) catGroups[n.category_id] = [];
        catGroups[n.category_id].push(n);
      });
      const catKeys = Object.keys(catGroups);
      const cols = Math.ceil(Math.sqrt(catKeys.length));

      // Dynamic cell size based on node dimensions
      const maxNodeW = Math.max(...nodes.map(n => (n._radius || 24) * 2.5), 60);
      const maxNodeH = Math.max(...nodes.map(n => (n._radius || 24) * 1.7), 40);
      const nodeSpacingX = maxNodeW + 18;
      const nodeSpacingY = maxNodeH + 10;

      nodes.forEach(d => {
        const catIdx = catKeys.indexOf(d.category_id);
        const col = catIdx % cols;
        const row = Math.floor(catIdx / cols);
        const catNodes = catGroups[d.category_id] || [];
        const nodeIdx = catNodes.indexOf(d);
        const nodesPerRow = Math.ceil(Math.sqrt(catNodes.length));
        const nodeCol = nodeIdx % nodesPerRow;
        const nodeRow = Math.floor(nodeIdx / nodesPerRow);
        const maxInCat = Math.max(...Object.values(catGroups).map(c => Math.ceil(Math.sqrt(c.length))));
        const cellW = maxInCat * nodeSpacingX + 60;
        const cellH = maxInCat * nodeSpacingY + 50;
        d.fx = cellW * col + 40 + nodeCol * nodeSpacingX;
        d.fy = cellH * row + 40 + nodeRow * nodeSpacingY;
      });
      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart();
      sim.alpha(0.01).restart();
      setTimeout(() => fitToView(true), 300);
    },

    // Layout: Org Chart — top-down tree from PMS
    layoutOrgChart: () => {
      setLayoutMode('orgchart');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'orgchart';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const links = linksDataRef.current;

      // Build adjacency from PMS outward (BFS tree)
      const rootId = 'pms';
      const adjacency = {};
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        if (!adjacency[sid]) adjacency[sid] = [];
        if (!adjacency[tid]) adjacency[tid] = [];
        adjacency[sid].push(tid);
        adjacency[tid].push(sid);
      });

      // BFS from root to assign levels
      const visited = new Set();
      const levels = {}; // nodeId -> level
      const queue = [rootId];
      visited.add(rootId);
      levels[rootId] = 0;
      while (queue.length > 0) {
        const current = queue.shift();
        const neighbors = adjacency[current] || [];
        for (const n of neighbors) {
          if (!visited.has(n)) {
            visited.add(n);
            levels[n] = levels[current] + 1;
            queue.push(n);
          }
        }
      }

      // Unvisited nodes get placed at the bottom
      const maxLevel = Math.max(...Object.values(levels), 0);
      nodes.forEach(n => {
        if (!visited.has(n.id)) levels[n.id] = maxLevel + 1;
      });

      // Group by level
      const byLevel = {};
      nodes.forEach(n => {
        const lvl = levels[n.id] ?? maxLevel + 1;
        if (!byLevel[lvl]) byLevel[lvl] = [];
        byLevel[lvl].push(n);
      });

      // Sort within levels by category for visual grouping
      Object.values(byLevel).forEach(arr => arr.sort((a, b) => (a.category_id || '').localeCompare(b.category_id || '')));

      const maxNodeW = Math.max(...nodes.map(n => (n._radius || 24) * 2.5), 60);
      const maxNodeH = Math.max(...nodes.map(n => (n._radius || 24) * 1.7), 40);
      const levelHeight = maxNodeH + 60;
      const nodeSpacing = maxNodeW + 20;
      const maxRowWidth = 20; // Max nodes before wrapping to next sub-row

      nodes.forEach(d => {
        const lvl = levels[d.id] ?? maxLevel + 1;
        const lvlNodes = byLevel[lvl];
        const idx = lvlNodes.indexOf(d);

        // Wrap wide levels into sub-rows
        const subRow = Math.floor(idx / maxRowWidth);
        const idxInSubRow = idx % maxRowWidth;
        const nodesInThisSubRow = Math.min(maxRowWidth, lvlNodes.length - subRow * maxRowWidth);
        const totalWidth = (nodesInThisSubRow - 1) * nodeSpacing;

        d.fx = -totalWidth / 2 + idxInSubRow * nodeSpacing;
        d.fy = lvl * levelHeight + subRow * 70;
      });

      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart();
      sim.alpha(0.01).restart();
      setTimeout(() => fitToView(true), 300);
    },

    // Layout: Clean 1 — cluster layout with pastel circle aesthetic
    layoutClean1: () => {
      setLayoutMode('clean1');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'clean1';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const links = linksDataRef.current;
      nodes.forEach(d => { d.fx = null; d.fy = null; });
      const { width, height } = dimensions;
      const cx = width / 2, cy = height / 2;

      const integCount = {};
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        integCount[sid] = (integCount[sid] || 0) + 1;
        integCount[tid] = (integCount[tid] || 0) + 1;
      });
      const maxInteg = Math.max(...Object.values(integCount), 1);

      sim.force('link', d3.forceLink(links).id(d => d.id).distance(100).strength(0.12));
      sim.force('charge', d3.forceManyBody().strength(-320).distanceMax(450));
      sim.force('collision', d3.forceCollide().radius(d => nodeRadiusFn(d) + 32).strength(1).iterations(3));

      sim.force('x', d3.forceX(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const ic = integCount[d.id] || 0;
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.3 * (1 - centerPull * 0.5);
        return cx + Math.cos(myAngle) * orbitRadius;
      }).strength(0.3));
      sim.force('y', d3.forceY(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const ic = integCount[d.id] || 0;
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.3 * (1 - centerPull * 0.5);
        return cy + Math.sin(myAngle) * orbitRadius;
      }).strength(0.3));

      nodes.forEach(d => { d.vx = (Math.random() - 0.5) * 8; d.vy = (Math.random() - 0.5) * 8; });
      sim.alpha(1).alphaTarget(0.1).restart();
      setTimeout(() => sim.alphaTarget(0), 2500);
      setTimeout(() => fitToView(true), 3000);
    },

    // Layout: Clean 2 — cluster layout with pastel rounded-rectangle aesthetic
    layoutClean2: () => {
      setLayoutMode('clean2');
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      layoutModeRef.current = 'clean2';
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const links = linksDataRef.current;
      nodes.forEach(d => { d.fx = null; d.fy = null; });
      const { width, height } = dimensions;
      const cx = width / 2, cy = height / 2;

      const integCount = {};
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        integCount[sid] = (integCount[sid] || 0) + 1;
        integCount[tid] = (integCount[tid] || 0) + 1;
      });
      const maxInteg = Math.max(...Object.values(integCount), 1);

      sim.force('link', d3.forceLink(links).id(d => d.id).distance(110).strength(0.1));
      sim.force('charge', d3.forceManyBody().strength(-350).distanceMax(500));
      sim.force('collision', d3.forceCollide().radius(d => nodeRadiusFn(d) + 35).strength(1).iterations(3));

      sim.force('x', d3.forceX(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const ic = integCount[d.id] || 0;
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.3 * (1 - centerPull * 0.5);
        return cx + Math.cos(myAngle) * orbitRadius;
      }).strength(0.3));
      sim.force('y', d3.forceY(d => {
        const myAngle = (CATEGORY_ANGLES[d.category_id] ?? 0) * Math.PI / 180;
        const ic = integCount[d.id] || 0;
        const centerPull = Math.min(ic / maxInteg, 0.7);
        const orbitRadius = Math.max(width, height) * 0.3 * (1 - centerPull * 0.5);
        return cy + Math.sin(myAngle) * orbitRadius;
      }).strength(0.3));

      nodes.forEach(d => { d.vx = (Math.random() - 0.5) * 8; d.vy = (Math.random() - 0.5) * 8; });
      sim.alpha(1).alphaTarget(0.1).restart();
      setTimeout(() => sim.alphaTarget(0), 2500);
      setTimeout(() => fitToView(true), 3000);
    },

    // Layout: Swimlane Categories — horizontal lanes by category
    layoutSwimCat: () => {
      setLayoutMode('swim-cat');
      layoutModeRef.current = 'swim-cat';
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;

      // Group nodes by category
      const catGroups = {};
      nodes.forEach(d => {
        const cat = d.category_id || 'uncategorized';
        if (!catGroups[cat]) catGroups[cat] = { name: d.category_name || cat, color: d.category_color || '#888', nodes: [] };
        catGroups[cat].nodes.push(d);
      });
      // Sort categories, sort nodes within by integration count desc
      const links = linksDataRef.current;
      const integCount = {};
      links.forEach(l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        integCount[sid] = (integCount[sid] || 0) + 1;
        integCount[tid] = (integCount[tid] || 0) + 1;
      });
      const sortedCats = Object.entries(catGroups).sort((a, b) => a[1].name.localeCompare(b[1].name));
      sortedCats.forEach(([, g]) => g.nodes.sort((a, b) => (integCount[b.id] || 0) - (integCount[a.id] || 0)));

      const laneHeight = 95;
      const nodeSpacing = 95;
      const labelWidth = 10;

      sortedCats.forEach(([catId, group], laneIdx) => {
        group.nodes.forEach((d, nodeIdx) => {
          d.fx = labelWidth + nodeIdx * nodeSpacing + 40;
          d.fy = laneIdx * laneHeight + laneHeight / 2;
        });
      });

      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart(); sim.alpha(0.01).restart();

      // Draw swimlane backgrounds
      setTimeout(() => {
        if (!svgRef.current) return;
        const svg = d3.select(svgRef.current);
        svg.selectAll('.swimlane-bg').remove();
        const g = svg.select('.graph-group');
        const laneG = g.insert('g', ':first-child').attr('class', 'swimlane-bg');
        const maxX = Math.max(...nodes.map(d => d.fx || 0)) + nodeSpacing;
        sortedCats.forEach(([catId, group], i) => {
          const y = i * laneHeight;
          laneG.append('rect')
            .attr('x', -250).attr('y', y + 3).attr('width', maxX + 320).attr('height', laneHeight - 6)
            .attr('fill', group.color).attr('fill-opacity', 0.06)
            .attr('stroke', group.color).attr('stroke-opacity', 0.25).attr('stroke-width', 1)
            .attr('rx', 14);
          laneG.append('rect')
            .attr('x', -240).attr('y', y + laneHeight / 2 - 13).attr('width', 208).attr('height', 26)
            .attr('rx', 13).attr('fill', group.color)
            .attr('filter', 'drop-shadow(0 1px 3px rgba(15,23,42,0.2))');
          const catLabel = (group.name || '').toUpperCase();
          laneG.append('text')
            .attr('x', -136).attr('y', y + laneHeight / 2 + 0.5)
            .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
            .attr('font-size', catLabel.length > 26 ? '8.5px' : '9.5px')
            .attr('fill', '#FFFFFF').attr('font-weight', '700')
            .attr('letter-spacing', '0.4px')
            .text(catLabel);
        });
        fitToView(true);
      }, 200);
    },

    // Layout: Swimlane Properties — horizontal lanes by property (property view: show platforms as lanes)
    layoutSwimProp: () => {
      setLayoutMode('swim-prop');
      layoutModeRef.current = 'swim-prop';
      userHasZoomedRef.current = false; setMapNeedsRefresh(false);
      if (!simulationRef.current || !nodesDataRef.current.length) return;
      const sim = simulationRef.current;
      const nodes = nodesDataRef.current;
      const links = linksDataRef.current;

      // For property view: group by whether node is used/has solutions
      // For overall view: just use category grouping but in a different visual
      const usedNodes = nodes.filter(n => n.used);
      const unusedNodes = nodes.filter(n => !n.used);

      const laneHeight = 95;
      const nodeSpacing = 95;

      // Lane 1: Deployed platforms
      // Lane 2: Available/Unused platforms
      const lanes = [
        { label: 'Deployed', nodes: usedNodes, color: '#10B981' },
        { label: 'Available', nodes: unusedNodes, color: '#94A3B8' },
      ];

      // Sort within each lane by category
      lanes.forEach(lane => {
        lane.nodes.sort((a, b) => (a.category_name || '').localeCompare(b.category_name || '') || (a.name || '').localeCompare(b.name || ''));
      });

      lanes.forEach((lane, laneIdx) => {
        lane.nodes.forEach((d, nodeIdx) => {
          d.fx = 10 + nodeIdx * nodeSpacing + 40;
          d.fy = laneIdx * laneHeight + laneHeight / 2;
        });
      });

      sim.force('link', null).force('charge', null).force('x', null).force('y', null);
      sim.alpha(0.3).restart(); sim.alpha(0.01).restart();

      // Draw swimlane backgrounds
      setTimeout(() => {
        if (!svgRef.current) return;
        const svg = d3.select(svgRef.current);
        svg.selectAll('.swimlane-bg').remove();
        const g = svg.select('.graph-group');
        const laneG = g.insert('g', ':first-child').attr('class', 'swimlane-bg');
        const maxX = Math.max(...nodes.map(d => d.fx || 0)) + nodeSpacing;
        lanes.forEach((lane, i) => {
          const y = i * laneHeight;
          laneG.append('rect')
            .attr('x', -250).attr('y', y + 3).attr('width', maxX + 320).attr('height', laneHeight - 6)
            .attr('fill', lane.color).attr('fill-opacity', 0.07)
            .attr('stroke', lane.color).attr('stroke-opacity', 0.25).attr('stroke-width', 1)
            .attr('rx', 14);
          laneG.append('rect')
            .attr('x', -240).attr('y', y + laneHeight / 2 - 13).attr('width', 208).attr('height', 26)
            .attr('rx', 13).attr('fill', lane.color)
            .attr('filter', 'drop-shadow(0 1px 3px rgba(15,23,42,0.2))');
          laneG.append('text')
            .attr('x', -136).attr('y', y + laneHeight / 2 + 0.5)
            .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
            .attr('font-size', '10px').attr('fill', '#FFFFFF').attr('font-weight', '700')
            .attr('letter-spacing', '0.5px')
            .text(`${lane.label.toUpperCase()} (${lane.nodes.length})`);
        });
        fitToView(true);
      }, 200);
    },
  }));

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let debounceTimer = null;
    const initialDims = { width: container.clientWidth, height: container.clientHeight };
    setDimensions(initialDims);

    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            setDimensions(prev => {
              // Only trigger full re-render for large changes (>150px) — not panel open/close
              const dw = Math.abs(width - prev.width);
              const dh = Math.abs(height - prev.height);
              if (dw > 150 || dh > 150) {
                return { width, height };
              }
              // For small changes, just update SVG viewBox without re-render
              if (svgRef.current) {
                svgRef.current.setAttribute('width', width);
                svgRef.current.setAttribute('height', height);
              }
              return prev; // Don't trigger re-render
            });
          }, 200);
        }
      }
    });
    ro.observe(container);
    return () => { ro.disconnect(); clearTimeout(debounceTimer); };
  }, []);

  // Main D3 render
  useEffect(() => {
    if (!data || !svgRef.current || dimensions.width === 0) return;

    const { nodes: rawNodes, links: rawLinks, categories } = data;
    const { width, height } = dimensions;
    const cx = width / 2;
    const cy = height / 2;

    // Filter nodes
    let graphNodes = rawNodes.map(n => ({ ...n }));
    if (showUsedOnly && selectedProperty !== 'overall') {
      graphNodes = graphNodes.filter(n => n.used);
    }

    // Hide unconnected: remove nodes that have zero links
    if (hideUnconnected) {
      const linkedIds = new Set();
      rawLinks.forEach(l => {
        linkedIds.add(l.source?.id || l.source);
        linkedIds.add(l.target?.id || l.target);
      });
      graphNodes = graphNodes.filter(n => linkedIds.has(n.id));
    }

    const filteredNodeIds = new Set(graphNodes.map(n => n.id));

    // Filter links
    const graphLinks = rawLinks
      .filter(l => filteredNodeIds.has(l.source?.id || l.source) && filteredNodeIds.has(l.target?.id || l.target))
      .map(l => ({
        ...l,
        source: l.source?.id || l.source,
        target: l.target?.id || l.target,
      }));

    nodesDataRef.current = graphNodes;
    linksDataRef.current = graphLinks;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Defs
    const defs = svg.append('defs');

    // Arrowhead for one-way — sleek filled closed arrowhead
    defs.append('marker')
      .attr('id', 'arrow-one')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 5)
      .attr('markerWidth', 3)
      .attr('markerHeight', 3)
      .attr('orient', 'auto-start-reverse')
      .append('path')
      .attr('d', 'M1,2 L9,5 L1,8 Z')
      .attr('fill', 'context-stroke')
      .attr('stroke', 'none');

    // Arrowhead for two-way end
    defs.append('marker')
      .attr('id', 'arrow-two-end')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 9)
      .attr('refY', 5)
      .attr('markerWidth', 3)
      .attr('markerHeight', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M1,2 L9,5 L1,8 Z')
      .attr('fill', 'context-stroke')
      .attr('stroke', 'none');

    // Arrowhead for two-way start
    defs.append('marker')
      .attr('id', 'arrow-two-start')
      .attr('viewBox', '0 0 10 10')
      .attr('refX', 1)
      .attr('refY', 5)
      .attr('markerWidth', 3)
      .attr('markerHeight', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M9,2 L1,5 L9,8 Z')
      .attr('fill', 'context-stroke')
      .attr('stroke', 'none');

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        // Track if user manually zoomed/panned (not programmatic)
        if (event.sourceEvent) {
          userHasZoomedRef.current = true;
        }
      });
    zoomRef.current = zoom;
    svg.call(zoom);

    const g = svg.append('g').attr('class', 'graph-group');

    // Grid
    const gridG = g.append('g').attr('class', 'grid');
    const gridRange = 3000;
    for (let x = -gridRange; x <= gridRange; x += 60) {
      gridG.append('line')
        .attr('x1', x).attr('y1', -gridRange)
        .attr('x2', x).attr('y2', gridRange)
        .attr('stroke', 'rgba(15,23,42,0.035)')
        .attr('stroke-width', 0.5);
    }
    for (let y = -gridRange; y <= gridRange; y += 60) {
      gridG.append('line')
        .attr('x1', -gridRange).attr('y1', y)
        .attr('x2', gridRange).attr('y2', y)
        .attr('stroke', 'rgba(15,23,42,0.035)')
        .attr('stroke-width', 0.5);
    }

    // Category centroids - tighter grouping for a compact layout
    const catMap = {};
    const clusterRadius = Math.max(width, height) * 0.3;
    categories.forEach(c => {
      const angle = (CATEGORY_ANGLES[c.id] ?? 0) * Math.PI / 180;
      catMap[c.id] = {
        x: cx + Math.cos(angle) * clusterRadius,
        y: cy + Math.sin(angle) * clusterRadius,
        color: c.color,
        name: c.name,
      };
    });

    // Category halo labels — curved arc labels on the OUTER edge of each group
    const hullG = g.append('g').attr('class', 'category-hulls');

    // Category labels removed — using color legend instead
    function updateCategoryHulls() {
      hullG.selectAll('*').remove();
      const mode = layoutModeRef.current;
      if (mode !== 'grid' && mode !== 'flowchart') return;

      // Draw category header labels above each column/block
      const catGroups = {};
      nodesDataRef.current.forEach(n => {
        if (!catGroups[n.category_id]) catGroups[n.category_id] = { nodes: [], color: n.category_color, name: n.category_name };
        catGroups[n.category_id].nodes.push(n);
      });

      Object.values(catGroups).forEach(cg => {
        if (!cg.nodes.length) return;
        const minX = Math.min(...cg.nodes.map(n => n.fx ?? n.x));
        const maxX = Math.max(...cg.nodes.map(n => n.fx ?? n.x));
        const minY = Math.min(...cg.nodes.map(n => n.fy ?? n.y));

        // Background pill
        const labelX = (minX + maxX) / 2;
        const labelY = minY - 22;

        hullG.append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('text-anchor', 'middle')
          .attr('fill', cg.color)
          .attr('font-size', '9px')
          .attr('font-weight', '700')
          .attr('font-family', 'IBM Plex Sans, sans-serif')
          .attr('letter-spacing', '0.05em')
          .attr('opacity', 0.7)
          .text(cg.name.toUpperCase());
      });
    }


    // Links — curved paths, thin, light gray, modern
    const linkG = g.append('g').attr('class', 'links');

    // Helper: generate a curved path between source and target that arcs slightly
    function linkPath(d) {
      const sx = d.source.x, sy = d.source.y;
      const tx = d.target.x, ty = d.target.y;
      const dx = tx - sx, dy = ty - sy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const sR = d.source._radius || 24;
      const tR = d.target._radius || 24;
      const angle = Math.atan2(ty - sy, tx - sx);
      const startX = sx + Math.cos(angle) * sR;
      const startY = sy + Math.sin(angle) * sR;
      const endX = tx - Math.cos(angle) * tR;
      const endY = ty - Math.sin(angle) * tR;

      const mode = layoutModeRef.current || 'category';

      // Geometric right-angle lines for grid/flow/radial modes
      if (mode === 'grid' || mode === 'flowchart' || mode === 'orgchart') {
        const midX = (startX + endX) / 2;
        // Use L-shaped connector: go horizontal to midpoint, then vertical, then horizontal
        if (Math.abs(dx) > Math.abs(dy)) {
          return `M${startX},${startY} L${midX},${startY} L${midX},${endY} L${endX},${endY}`;
        } else {
          const midY = (startY + endY) / 2;
          return `M${startX},${startY} L${startX},${midY} L${endX},${midY} L${endX},${endY}`;
        }
      }

      // Gentle curve for force/radial modes
      const curvature = 0.12;
      const mx = (sx + tx) / 2 + dy * curvature;
      const my = (sy + ty) / 2 - dx * curvature;
      return `M${startX},${startY} Q${mx},${my} ${endX},${endY}`;
    }

    // Combine platform links and product links
    const allLinks = [...graphLinks];
    const productLinks = (data.product_links || [])
      .filter(l => filteredNodeIds.has(l.source) && filteredNodeIds.has(l.target))
      .map(l => ({ ...l }));

    const linkElements = linkG.selectAll('path.platform-link')
      .data(graphLinks)
      .join('path')
      .attr('class', 'link-line platform-link')
      .attr('fill', 'none')
      .attr('stroke', d => {
        if (d.integration_status === 'gap') return '#EF4444';
        return d.direction === 'two-way' ? '#3D5A6E' : '#506878';
      })
      .attr('stroke-width', d => {
        if (d.integration_status === 'gap') return 2.5;
        return d.direction === 'two-way' ? 2.5 : 2.2;
      })
      .attr('stroke-dasharray', d => {
        if (d.integration_status === 'gap') return '6 4';
        return d.direction === 'two-way' ? 'none' : '4 3';
      })
      .attr('stroke-opacity', d => d.integration_status === 'gap' ? 0.7 : 0.8)
      .attr('marker-end', d => d.direction === 'two-way' ? 'url(#arrow-two-end)' : 'url(#arrow-one)')
      .attr('marker-start', d => d.direction === 'two-way' ? 'url(#arrow-two-start)' : 'none');

    // Product-level integration links (rendered on top)
    const prodLinkElements = linkG.selectAll('path.product-link')
      .data(productLinks)
      .join('path')
      .attr('class', 'link-line product-link')
      .attr('fill', 'none')
      .attr('stroke', d => {
        if (d.integration_status === 'active') return '#6DCCB1';
        if (d.integration_status === 'possible') return '#3B82F6';
        return '#F59E0B';
      })
      .attr('stroke-width', 3.5)
      .attr('stroke-dasharray', d => {
        if (d.integration_status === 'active') return 'none';
        if (d.integration_status === 'possible') return '8 4';
        return '5 3';
      })
      .attr('marker-end', d => d.direction === 'two-way' ? 'url(#arrow-two-end)' : 'url(#arrow-one)')
      .attr('marker-start', d => d.direction === 'two-way' ? 'url(#arrow-two-start)' : 'none');

    // ─── Text-wrapping + dynamic circle sizing ───
    const nodeG = g.append('g').attr('class', 'nodes');
    const PADDING = 6;  // px padding inside circle around text

    // Measure text width using a temporary SVG element
    const measureG = svg.append('g').attr('class', 'measure').style('visibility', 'hidden');
    function measureText(text, fontSize, fontFamily, fontWeight) {
      const t = measureG.append('text')
        .attr('font-size', fontSize + 'px')
        .attr('font-family', fontFamily)
        .attr('font-weight', fontWeight || '600')
        .text(text);
      const w = t.node().getComputedTextLength();
      t.remove();
      return w;
    }

    // Word-wrap a string into lines that fit within maxWidth pixels
    function wrapText(str, maxWidth, fontSize, fontFamily, fontWeight) {
      if (!str) return [];
      // Split on spaces, slashes, ampersands
      const tokens = str.split(/(\s+|(?=[\/&]))/g).filter(t => t.trim());
      const lines = [];
      let currentLine = '';
      for (const token of tokens) {
        const candidate = currentLine ? (currentLine + ' ' + token) : token;
        const w = measureText(candidate, fontSize, fontFamily, fontWeight);
        if (w > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = token;
        } else {
          currentLine = candidate;
        }
      }
      if (currentLine) lines.push(currentLine);
      return lines;
    }

    // Pre-compute each node's wrapped text lines + radius
    const platformFontSize = (d) => d.importance === 3 ? 12.5 : d.importance === 2 ? 11.5 : 10.5;
    const vendorFontSize   = (d) => d.importance === 3 ? 10 : d.importance === 2 ? 9 : 8.5;
    const platformLineH    = (d) => platformFontSize(d) + 3;  // line height
    const vendorLineH      = (d) => vendorFontSize(d) + 2.5;

    graphNodes.forEach(d => {
      // First pass: try wrapping at a generous target width
      const targetWidth = d.importance === 3 ? 66 : d.importance === 2 ? 56 : 46;
      const pLines = wrapText(d.short, targetWidth, platformFontSize(d), FONT_PLATFORM, '600');
      const vLines = d.vendor_name
        ? wrapText(d.vendor_name, targetWidth, vendorFontSize(d), FONT_VENDOR, '500')
        : [];

      d._pLines = pLines;
      d._vLines = vLines;

      // Measure widest line
      let maxW = 0;
      for (const l of pLines) {
        maxW = Math.max(maxW, measureText(l, platformFontSize(d), FONT_PLATFORM, '600'));
      }
      for (const l of vLines) {
        maxW = Math.max(maxW, measureText(l, vendorFontSize(d), FONT_VENDOR, '500'));
      }

      // Total text block height
      const gap = vLines.length > 0 ? 3 : 0; // gap between platform name and vendor
      const totalH = pLines.length * platformLineH(d) + gap + vLines.length * vendorLineH(d);

      // Radius: half-diagonal of bounding box + padding, ensure minimum
      const halfW = maxW / 2 + PADDING;
      const halfH = totalH / 2 + PADDING;
      const minR = d.importance === 3 ? 28 : d.importance === 2 ? 22 : 18;
      d._radius = Math.max(Math.sqrt(halfW * halfW + halfH * halfH), minR);
    });

    // Clean up measure group
    measureG.remove();

    const nodeRadius = (d) => d._radius;

    const nodeGroups = nodeG.selectAll('g')
      .data(graphNodes, d => d.id)
      .join('g')
      .attr('class', 'node-group')
      .style('cursor', 'pointer')
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulationRef.current?.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulationRef.current?.alphaTarget(0);
        })
      );

    // Circle — dynamically sized to text
    // For multi-solution nodes, add stacked shadow circles behind
    nodeGroups.each(function(d) {
      const group = d3.select(this);
      const count = d.solution_count || 0;
      if (count > 1) {
        // Draw offset shadow circles for stack effect (max 3 visible)
        const stackCount = Math.min(count - 1, 3);
        for (let i = stackCount; i >= 1; i--) {
          group.append('circle')
            .attr('class', 'stack-circle')
            .attr('r', nodeRadius(d) - 1)
            .attr('cx', i * 4)
            .attr('cy', -i * 3)
            .attr('fill', '#FFFFFF')
            .attr('stroke', d.category_color)
            .attr('stroke-width', 1.5)
            .attr('stroke-opacity', 0.4)
            .attr('opacity', 0.6);
        }
      }
    });

    nodeGroups.append('circle')
      .attr('class', 'node-circle')
      .attr('r', nodeRadius)
      .attr('fill', d => d.used ? '#FFFFFF' : '#F8FAFC')
      .attr('stroke', d => d.used ? d.category_color : 'rgba(15,23,42,0.18)')
      .attr('stroke-width', d => d.used ? 2 : 1.5)
      .attr('stroke-dasharray', d => d.used ? 'none' : '6 4')
      .attr('filter', d => d.used ? 'drop-shadow(0 2px 5px rgba(15,23,42,0.14))' : 'none');

    // Rectangular node shape (hidden by default — shown in flow/grid modes)
    nodeGroups.append('rect')
      .attr('class', 'node-rect')
      .attr('width', d => nodeRadius(d) * 2.4)
      .attr('height', d => nodeRadius(d) * 1.6)
      .attr('x', d => -nodeRadius(d) * 1.2)
      .attr('y', d => -nodeRadius(d) * 0.8)
      .attr('rx', 14)
      .attr('ry', 14)
      .attr('fill', d => d.used ? '#FFFFFF' : '#F8FAFC')
      .attr('stroke', d => d.used ? d.category_color : 'rgba(15,23,42,0.18)')
      .attr('stroke-width', d => d.used ? 1.75 : 1.5)
      .attr('stroke-dasharray', d => d.used ? 'none' : '6 4')
      .attr('filter', d => d.used ? 'drop-shadow(0 2px 6px rgba(15,23,42,0.14))' : 'none')
      .style('display', 'none');

    // Multi-solution count badge
    nodeGroups.each(function(d) {
      const group = d3.select(this);
      const count = d.solution_count || 0;
      if (count > 1) {
        const r = nodeRadius(d);
        const badgeX = r * 0.65;
        const badgeY = -r * 0.65;
        group.append('circle')
          .attr('class', 'solution-count-bg')
          .attr('cx', badgeX)
          .attr('cy', badgeY)
          .attr('r', 8)
          .attr('fill', d.category_color)
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', 1.5);
        group.append('text')
          .attr('class', 'solution-count-label')
          .attr('x', badgeX)
          .attr('y', badgeY + 0.5)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '8px')
          .attr('font-weight', '700')
          .attr('fill', '#FFFFFF')
          .attr('font-family', FONT_PLATFORM)
          .text(count);
      }
    });

    // Brand designation icons on nodes
    nodeGroups.each(function(d) {
      const group = d3.select(this);
      const brands = d.brand_designations || [];
      if (brands.length === 0) return;
      const r = nodeRadius(d);
      // Place brand dots along the bottom of the node
      brands.slice(0, 3).forEach((bd, i) => {
        const bx = -((brands.length - 1) * 6) / 2 + i * 6;
        const by = r + 6;
        group.append('circle')
          .attr('class', 'brand-icon')
          .attr('cx', bx)
          .attr('cy', by)
          .attr('r', 3.5)
          .attr('fill', bd.brand_color || '#6366F1')
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', 1);
        group.append('text')
          .attr('x', bx)
          .attr('y', by + 0.5)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', '4px')
          .attr('font-weight', '800')
          .attr('fill', '#FFFFFF')
          .attr('font-family', FONT_PLATFORM)
          .text(bd.brand_short?.[0] || '');
      });
    });

    // Vendor logos — watermark (background fill) + badge (top rim), toggled by style
    nodeGroups.each(function(d) {
      if (!d.used) return;
      const logoUrl = vendorLogoUrl(d);
      if (!logoUrl) return;
      const r = nodeRadius(d);
      const group = d3.select(this);
      const side = r * 1.3;
      group.append('image')
        .attr('class', 'vendor-logo-bg')
        .attr('href', logoUrl)
        .attr('x', -side / 2).attr('y', -side / 2)
        .attr('width', side).attr('height', side)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('opacity', logoOpacityRef.current)
        .style('display', (layoutModeRef.current === 'logo-cluster' || layoutModeRef.current === 'logo-cluster-soft') ? 'none' : (logoStyleRef.current === 'watermark' ? null : 'none'))
        .style('pointer-events', 'none')
        .on('error', function() { d3.select(this).remove(); });
      const badge = group.append('g')
        .attr('class', 'vendor-logo-badge')
        .attr('transform', `translate(0, ${-r})`)
        .style('display', (layoutModeRef.current === 'logo-cluster' || layoutModeRef.current === 'logo-cluster-soft') ? 'none' : (logoStyleRef.current === 'badge' ? null : 'none'))
        .style('pointer-events', 'none');
      badge.append('circle')
        .attr('r', 13)
        .attr('fill', '#FFFFFF')
        .attr('stroke', '#E2E8F0')
        .attr('stroke-width', 1)
        .attr('filter', 'drop-shadow(0 1px 3px rgba(15,23,42,0.18))');
      badge.append('image')
        .attr('href', logoUrl)
        .attr('x', -10).attr('y', -10)
        .attr('width', 20).attr('height', 20)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('opacity', Math.max(logoOpacityRef.current, 0.15))
        .on('error', function() { d3.select(this.parentNode).remove(); });
    });

    // Logo Cluster elements — logo badge at top rim, product name centered,
    // platform type curved along the inner bottom arc (shown only in logo-cluster mode)
    const lfCanvas = document.createElement('canvas');
    const lfCtx = lfCanvas.getContext('2d');
    nodeGroups.each(function(d) {
      const r = nodeRadius(d);
      const group = d3.select(this).append('g')
        .attr('class', 'lf-group')
        .style('display', (layoutModeRef.current === 'logo-cluster' || layoutModeRef.current === 'logo-cluster-soft') ? null : 'none')
        .style('pointer-events', 'none');

      // Platform type name along the bottom inner arc
      const ra = r - 6;
      const arcId = `lf-arc-${d.id}`;
      const ax = (0.978 * ra).toFixed(1);
      const ay = (0.208 * ra).toFixed(1);
      group.append('path')
        .attr('id', arcId)
        .attr('d', `M ${-ax} ${ay} A ${ra} ${ra} 0 0 0 ${ax} ${ay}`)
        .attr('fill', 'none');
      let platLabel = (d.short || d.name || '').toUpperCase();
      const arcLen = ra * 2.72; // ~156° arc
      let afs = d.importance === 3 ? 8 : 7.5;
      const arcWidth = (txt, size) => {
        lfCtx.font = `700 ${size}px 'IBM Plex Sans', sans-serif`;
        return lfCtx.measureText(txt).width + txt.length * size * 0.06;
      };
      while (afs > 6 && arcWidth(platLabel, afs) > arcLen * 0.92) afs -= 0.5;
      while (platLabel.length > 6 && arcWidth(platLabel + '…', afs) > arcLen * 0.92) platLabel = platLabel.slice(0, -1);
      if (platLabel !== (d.short || d.name || '').toUpperCase()) platLabel += '…';
      group.append('text')
        .attr('font-size', afs + 'px')
        .attr('font-weight', '700')
        .attr('letter-spacing', '0.06em')
        .attr('fill', d.category_color)
        .attr('font-family', FONT_PLATFORM)
        .append('textPath')
        .attr('href', `#${arcId}`)
        .attr('startOffset', '50%')
        .attr('text-anchor', 'middle')
        .text(platLabel);

      // Product name centered (up to 2 wrapped lines)
      const prodName = d.used ? (d.product_name || d.vendor_name || '—') : 'Not assigned';
      const fs = d.importance === 3 ? 11.5 : d.importance === 2 ? 10.5 : 9.5;
      lfCtx.font = `700 ${fs}px 'IBM Plex Sans', sans-serif`;
      const maxW = r * 1.45;
      const words = prodName.split(/\s+/);
      const lines = [];
      let cur = '';
      for (const w of words) {
        const test = cur ? cur + ' ' + w : w;
        if (lfCtx.measureText(test).width <= maxW || !cur) cur = test;
        else { lines.push(cur); cur = w; if (lines.length === 2) break; }
      }
      if (lines.length < 2 && cur) lines.push(cur);
      if (lines.length > 2) lines.length = 2;
      const origLast = lines[lines.length - 1] || '';
      let last = origLast;
      while (last && lfCtx.measureText(last + '…').width > maxW && last.length > 3) last = last.slice(0, -1);
      if (last !== origLast) lines[lines.length - 1] = last + '…';

      const lfText = group.append('text')
        .attr('text-anchor', 'middle')
        .attr('font-size', fs + 'px')
        .attr('font-weight', '700')
        .attr('fill', d.used ? 'hsl(222,47%,11%)' : 'hsl(215,20%,55%)')
        .attr('font-style', d.used ? null : 'italic')
        .attr('font-family', FONT_PLATFORM);
      const lh = fs * 1.15;
      const startY = -((lines.length - 1) * lh) / 2 + fs * 0.35 - 2;
      lines.forEach((line, i) => {
        lfText.append('tspan').attr('x', 0).attr('y', startY + i * lh).text(line);
      });

      // Logo badge in its own circle at the top rim
      const isSoftMode = layoutModeRef.current === 'logo-cluster-soft';
      const logoUrl = d.used ? vendorLogoUrl(d, { transparent: isSoftMode }) : null;
      if (logoUrl) {
        const bR = Math.max(12, r * 0.32);
        const badge = group.append('g').attr('transform', `translate(0, ${-r})`);
        badge.append('circle')
          .attr('class', 'lf-badge-ring')
          .attr('r', bR)
          .attr('fill', '#FFFFFF')
          .attr('stroke', d.category_color)
          .attr('stroke-width', 1.5)
          .attr('filter', 'drop-shadow(0 1px 3px rgba(15,23,42,0.18))')
          .style('display', isSoftMode ? 'none' : null);
        const iS = bR * 1.4;
        badge.append('image')
          .attr('href', logoUrl)
          .attr('x', -iS / 2).attr('y', -iS / 2)
          .attr('width', iS).attr('height', iS)
          .attr('preserveAspectRatio', 'xMidYMid meet')
          .on('error', function() { d3.select(this).remove(); });
      }

      // Multi-platform "linked" chip — small violet badge at bottom-right
      // that appears whenever this node's product spans multiple platform types.
      if (d.is_multi_platform_product && d.used) {
        const chipR = Math.max(9, r * 0.24);
        const angle = Math.PI / 4; // bottom-right
        const cx = Math.cos(angle) * r;
        const cy = Math.sin(angle) * r;
        const chip = group.append('g')
          .attr('class', 'lf-multi-platform-chip')
          .attr('transform', `translate(${cx}, ${cy})`)
          .style('cursor', 'help');
        chip.append('circle')
          .attr('r', chipR)
          .attr('fill', '#7c3aed')
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', 1.5)
          .attr('filter', 'drop-shadow(0 1px 2px rgba(15,23,42,0.25))');
        chip.append('text')
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', `${Math.max(7, chipR)}px`)
          .attr('font-weight', '700')
          .attr('fill', '#FFFFFF')
          .attr('font-family', 'IBM Plex Mono, monospace')
          .text(`${(d.product_platform_ids || []).length}×`);
      }
      // Shadow node (mirrored from another platform) — dashed ring to differentiate
      if (d.is_shadow) {
        group.select('.node-circle')
          .attr('stroke-dasharray', '3,3')
          .attr('stroke', '#7c3aed');
      }

      // D1. Bulk-selected — thick violet ring behind the node
      if (Array.isArray(bulkSelectedIds) && bulkSelectedIds.includes(d.id)) {
        group.insert('circle', ':first-child')
          .attr('class', 'lf-bulk-halo')
          .attr('r', r + 6)
          .attr('fill', 'none')
          .attr('stroke', '#7c3aed')
          .attr('stroke-width', 3)
          .attr('opacity', 0.75)
          .attr('stroke-dasharray', '4,3');
      }
    });

    // Platform label — wrapped multi-line
    nodeGroups.each(function(d) {
      const group = d3.select(this);
      const pLines = d._pLines;
      const vLines = d._vLines;
      const pFontSz = platformFontSize(d);
      const vFontSz = vendorFontSize(d);
      const pLH = platformLineH(d);
      const vLH = vendorLineH(d);
      const gap = vLines.length > 0 ? 3 : 0;
      const totalH = pLines.length * pLH + gap + vLines.length * vLH;
      let startY = -totalH / 2 + pFontSz * 0.7; // align text from top of block

      // Platform name lines
      const pText = group.append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('font-size', pFontSz + 'px')
        .attr('font-weight', '600')
        .attr('fill', d.used ? 'hsl(222,47%,11%)' : 'hsl(215,20%,35%)')
        .attr('font-family', FONT_PLATFORM)
        .style('display', (layoutModeRef.current === 'logo-cluster' || layoutModeRef.current === 'logo-cluster-soft') ? 'none' : null);

      pLines.forEach((line, i) => {
        pText.append('tspan')
          .attr('x', 0)
          .attr('dy', i === 0 ? startY + 'px' : pLH + 'px')
          .text(line);
      });

      // Vendor name lines
      if (vLines.length > 0) {
        const vText = group.append('text')
          .attr('class', 'node-vendor-label')
          .attr('text-anchor', 'middle')
          .attr('font-size', vFontSz + 'px')
          .attr('font-weight', '500')
          .attr('fill', d.category_color)
          .attr('font-family', FONT_VENDOR)
          .style('display', (layoutModeRef.current === 'logo-cluster' || layoutModeRef.current === 'logo-cluster-soft') ? 'none' : null);

        vLines.forEach((line, i) => {
          vText.append('tspan')
            .attr('x', 0)
            .attr('dy', i === 0 ? (startY + pLines.length * pLH + gap) + 'px' : vLH + 'px')
            .text(line);
        });
      }
    });

    // Click — supports Shift-click for multi-select (D1. Bulk Stack Assign)
    nodeGroups.on('click', (event, d) => {
      event.stopPropagation();
      onNodeSelectRef.current(d, { shiftKey: !!event.shiftKey, metaKey: !!event.metaKey, ctrlKey: !!event.ctrlKey });
    });

    // Hover
    nodeGroups.on('mouseenter', (event, d) => {
      linkElements.attr('opacity', l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        return (sid === d.id || tid === d.id) ? 1 : 0.06;
      });
      linkElements.attr('stroke-width', l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        if (sid === d.id || tid === d.id) return l.direction === 'two-way' ? 1.8 : 1.4;
        return l.direction === 'two-way' ? 1 : 0.75;
      });
      linkElements.attr('stroke', l => {
        const sid = typeof l.source === 'object' ? l.source.id : l.source;
        const tid = typeof l.target === 'object' ? l.target.id : l.target;
        if (sid === d.id || tid === d.id) return '#64748B';
        return l.direction === 'two-way' ? '#B0BEC5' : '#CFD8DC';
      });

      if (tooltipRef.current) {
        const tt = tooltipRef.current;
        tt.style.display = 'block';
        const solutionsList = (d.solutions || []).length > 0
          ? d.solutions.map(s => {
              const otherPlats = (s.product_platforms || []).filter(pp => pp.id !== d.id);
              const multiChip = otherPlats.length > 0
                ? ` <span style="display:inline-flex;align-items:center;gap:2px;background:#f5f3ff;color:#7c3aed;border:1px solid #ddd6fe;border-radius:6px;padding:0 4px;font-size:8px;font-weight:600;margin-left:3px;" title="Also in ${otherPlats.map(p => p.name).join(', ')}">⇱ ${otherPlats.length + 1}×</span>`
                : '';
              return `<div style="font-family:IBM Plex Mono,monospace;font-size:10px;color:#475569;padding:1px 0;">${s.product_name || s.vendor_name || 'Unnamed'}${multiChip}${s.product_status ? ` <span style="color:${s.product_status === 'Deployed' ? '#059669' : '#D97706'};font-size:9px;">(${s.product_status})</span>` : ''}${s.is_shadow ? ` <span style="color:#8b5cf6;font-size:8px;font-style:italic;">· linked from ${s.shadow_of_platform_name || ''}</span>` : ''}</div>`;
            }).join('')
          : '';
        const primarySol = (d.solutions || [])[0];
        const otherPlats = primarySol ? (primarySol.product_platforms || []).filter(pp => pp.id !== d.id) : [];
        const multiLine = otherPlats.length > 0
          ? `<div style="font-size:9px;color:#7c3aed;margin-top:3px;font-style:italic;">Also lives under ${otherPlats.map(p => p.name).join(', ')}</div>`
          : '';
        tt.innerHTML = `
          <div style="font-weight:600;font-size:12px;margin-bottom:3px;">${d.name}</div>
          <div style="color:${d.category_color};font-size:10px;margin-bottom:3px;">${d.category_name}</div>
          ${d.solution_count > 1 ? `<div style="font-size:10px;color:#3B82F6;margin-bottom:3px;font-weight:600;">${d.solution_count} solutions</div>` : ''}
          ${solutionsList || (d.vendor_name ? `<div style="font-family:IBM Plex Mono,monospace;font-size:10px;color:#475569;">Vendor: ${d.vendor_name}</div>` : '')}
          ${multiLine}
          ${!d.used ? '<div style="color:#94a3b8;font-size:9px;margin-top:3px;">Not used at this property</div>' : ''}
        `;
        tt.style.left = (event.clientX + 14) + 'px';
        tt.style.top = (event.clientY - 12) + 'px';
      }
    });

    nodeGroups.on('mousemove', (event) => {
      if (tooltipRef.current) {
        tooltipRef.current.style.left = (event.clientX + 14) + 'px';
        tooltipRef.current.style.top = (event.clientY - 12) + 'px';
      }
    });

    nodeGroups.on('mouseleave', () => {
      linkElements
        .attr('opacity', 1)
        .attr('stroke-width', d => d.direction === 'two-way' ? 1 : 0.75)
        .attr('stroke', d => d.direction === 'two-way' ? '#B0BEC5' : '#CFD8DC');
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    });

    svg.on('click', () => {
      if (onCanvasClickRef.current) {
        onCanvasClickRef.current();
      } else {
        onNodeSelectRef.current(null);
      }
    });

    // Force simulation — integration-weighted: highly-connected nodes closer to center
    // Count integrations per node for weighting
    const initIntegCount = {};
    graphLinks.forEach(l => {
      const sid = typeof l.source === 'object' ? l.source.id : l.source;
      const tid = typeof l.target === 'object' ? l.target.id : l.target;
      initIntegCount[sid] = (initIntegCount[sid] || 0) + 1;
      initIntegCount[tid] = (initIntegCount[tid] || 0) + 1;
    });
    const initMaxInteg = Math.max(...Object.values(initIntegCount), 1);

    // Detect bridge nodes (connecting multiple categories)
    const initCrossCats = {};
    graphLinks.forEach(l => {
      const s = typeof l.source === 'object' ? l.source : graphNodes.find(n => n.id === l.source);
      const t = typeof l.target === 'object' ? l.target : graphNodes.find(n => n.id === l.target);
      if (!s || !t) return;
      if ((s.category_id || '') !== (t.category_id || '')) {
        if (!initCrossCats[s.id]) initCrossCats[s.id] = new Set();
        if (!initCrossCats[t.id]) initCrossCats[t.id] = new Set();
        initCrossCats[s.id].add(t.category_id);
        initCrossCats[t.id].add(s.category_id);
      }
    });

    const sim = d3.forceSimulation(graphNodes)
      .force('link', d3.forceLink(graphLinks).id(d => d.id).distance(80).strength(0.15))
      .force('charge', d3.forceManyBody().strength(-280).distanceMax(400))
      .force('collision', d3.forceCollide().radius(d => nodeRadius(d) + 28).strength(1).iterations(3))
      .force('x', d3.forceX(d => {
        const basePos = catMap[d.category_id]?.x || cx;
        const ic = initIntegCount[d.id] || 0;
        const centerPull = Math.min(ic / initMaxInteg, 0.7);
        const crossCats = initCrossCats[d.id];
        if (crossCats && crossCats.size > 0) {
          let sumX = basePos;
          let count = 1;
          crossCats.forEach(catId => {
            sumX += (catMap[catId]?.x || cx) * 0.3;
            count += 0.3;
          });
          return cx + (sumX / count - cx) * (1 - centerPull * 0.5);
        }
        return cx + (basePos - cx) * (1 - centerPull * 0.5);
      }).strength(d => {
        const ic = initIntegCount[d.id] || 0;
        return 0.35 + Math.min(ic / initMaxInteg, 0.5) * 0.2;
      }))
      .force('y', d3.forceY(d => {
        const basePos = catMap[d.category_id]?.y || cy;
        const ic = initIntegCount[d.id] || 0;
        const centerPull = Math.min(ic / initMaxInteg, 0.7);
        const crossCats = initCrossCats[d.id];
        if (crossCats && crossCats.size > 0) {
          let sumY = basePos;
          let count = 1;
          crossCats.forEach(catId => {
            sumY += (catMap[catId]?.y || cy) * 0.3;
            count += 0.3;
          });
          return cy + (sumY / count - cy) * (1 - centerPull * 0.5);
        }
        return cy + (basePos - cy) * (1 - centerPull * 0.5);
      }).strength(d => {
        const ic = initIntegCount[d.id] || 0;
        return 0.35 + Math.min(ic / initMaxInteg, 0.5) * 0.2;
      }))
      .alphaDecay(0.045)
      .velocityDecay(0.55);

    simulationRef.current = sim;

    let tickCount = 0;
    sim.on('tick', () => {
      tickCount++;
      linkElements.attr('d', linkPath);
      prodLinkElements.attr('d', d => {
        // Product links use the same path but slightly offset for visibility
        const src = graphNodes.find(n => n.id === (typeof d.source === 'object' ? d.source.id : d.source));
        const tgt = graphNodes.find(n => n.id === (typeof d.target === 'object' ? d.target.id : d.target));
        if (!src || !tgt) return '';
        return linkPath({ source: src, target: tgt, _radius: src._radius, direction: d.direction });
      });

      nodeGroups.attr('transform', d => `translate(${d.x},${d.y})`);

      // Update curved category labels at intervals
      if (tickCount === 20 || tickCount === 60 || tickCount === 120 || tickCount === 200) {
        updateCategoryHulls();
        fitToView();
      }
    });

    sim.on('end', () => {
      updateCategoryHulls();
      fitToView();
    });

    // Re-apply current layout mode if not the default force layout
    const pendingMode = layoutModeRef.current;
    if (pendingMode && pendingMode !== 'category') {
      setTimeout(() => {
        const s = simulationRef.current;
        const n = nodesDataRef.current;
        if (!s || !n.length) return;

        const catGroups = {};
        n.forEach(nd => { if (!catGroups[nd.category_id]) catGroups[nd.category_id] = []; catGroups[nd.category_id].push(nd); });
        const catKeys = Object.keys(catGroups);

        const applyDeterministic = () => {
          s.force('link', null).force('charge', null).force('x', null).force('y', null);
          s.alpha(0.3).restart(); s.alpha(0.01).restart();
          setTimeout(() => fitToView(true), 300);
        };

        const maxNW = Math.max(...n.map(nd => (nd._radius || 24) * 2.5), 60);
        const maxNH = Math.max(...n.map(nd => (nd._radius || 24) * 1.7), 40);

        if (pendingMode === 'grid') {
          const colW = maxNW + 24, rowH = maxNH + 12;
          n.forEach(d => { const ci = catKeys.indexOf(d.category_id); const ni = (catGroups[d.category_id] || []).indexOf(d); d.fx = 80 + ci * colW; d.fy = 40 + ni * rowH; });
          applyDeterministic();
        } else if (pendingMode === 'flowchart') {
          const cols = Math.ceil(Math.sqrt(catKeys.length));
          const nsX = maxNW + 18, nsY = maxNH + 10;
          const maxInCat = Math.max(...Object.values(catGroups).map(c => Math.ceil(Math.sqrt(c.length))));
          const cW = maxInCat * nsX + 60, cH = maxInCat * nsY + 50;
          n.forEach(d => { const ci = catKeys.indexOf(d.category_id); const col = ci % cols; const row = Math.floor(ci / cols); const cn = catGroups[d.category_id] || []; const ni = cn.indexOf(d); const npr = Math.ceil(Math.sqrt(cn.length)); d.fx = cW * col + 40 + (ni % npr) * nsX; d.fy = cH * row + 40 + Math.floor(ni / npr) * nsY; });
          applyDeterministic();
        } else if (pendingMode === 'radial') {
          const cxR = width / 2, cyR = height / 2;
          const radStep = Math.max(...n.map(nd => nd._radius || 24), 20) * 2.2;
          n.forEach(d => { const ci = catKeys.indexOf(d.category_id); const cn = catGroups[d.category_id] || []; const ni = cn.indexOf(d); const wa = (Math.PI * 2) / catKeys.length; const sa = ci * wa - Math.PI / 2; const as = wa * 0.7; const na = sa + (as * (ni / Math.max(cn.length - 1, 1))) + (wa * 0.15); const r = 140 + ni * radStep; d.fx = cxR + Math.cos(na) * r; d.fy = cyR + Math.sin(na) * r; });
          applyDeterministic();
        } else if (pendingMode === 'orgchart') {
          const adj = {}; const lnks = linksDataRef.current;
          lnks.forEach(l => { const si = typeof l.source === 'object' ? l.source.id : l.source; const ti = typeof l.target === 'object' ? l.target.id : l.target; if (!adj[si]) adj[si] = []; if (!adj[ti]) adj[ti] = []; adj[si].push(ti); adj[ti].push(si); });
          const vis = new Set(); const lvls = {}; const q = ['pms']; vis.add('pms'); lvls['pms'] = 0;
          while (q.length > 0) { const c = q.shift(); for (const nb of (adj[c] || [])) { if (!vis.has(nb)) { vis.add(nb); lvls[nb] = lvls[c] + 1; q.push(nb); } } }
          const ml = Math.max(...Object.values(lvls), 0);
          n.forEach(nd => { if (!vis.has(nd.id)) lvls[nd.id] = ml + 1; });
          const byLvl = {}; n.forEach(nd => { const lv = lvls[nd.id] ?? ml + 1; if (!byLvl[lv]) byLvl[lv] = []; byLvl[lv].push(nd); });
          Object.values(byLvl).forEach(arr => arr.sort((a, b) => (a.category_id || '').localeCompare(b.category_id || '')));
          const nSp = maxNW + 20; const lH = maxNH + 60;
          n.forEach(d => { const lv = lvls[d.id] ?? ml + 1; const la = byLvl[lv]; const idx = la.indexOf(d); const sr = Math.floor(idx / 20); const isr = idx % 20; const nisr = Math.min(20, la.length - sr * 20); const tw = (nisr - 1) * nSp; d.fx = -tw / 2 + isr * nSp; d.fy = lv * lH + sr * (maxNH + 30); });
          applyDeterministic();
        } else if (pendingMode === 'pms-hub') {
          n.forEach(d => { d.fx = null; d.fy = null; });
          setLayoutMode('pms-hub');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'clean1') {
          n.forEach(d => { d.fx = null; d.fy = null; });
          setLayoutMode('clean1');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'clean2') {
          n.forEach(d => { d.fx = null; d.fy = null; });
          setLayoutMode('clean2');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'logo-cluster') {
          n.forEach(d => { d.fx = null; d.fy = null; });
          setLayoutMode('logo-cluster');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'logo-cluster-soft') {
          n.forEach(d => { d.fx = null; d.fy = null; });
          setLayoutMode('logo-cluster-soft');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'swim-cat') {
          setLayoutMode('swim-cat');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        } else if (pendingMode === 'swim-prop') {
          setLayoutMode('swim-prop');
          userHasZoomedRef.current = false; setMapNeedsRefresh(false);
        }
      }, 100);
    }

    return () => {
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dimensions, showUsedOnly, hideUnconnected, selectedProperty]);


  // Update node text when labelSwap changes
  useEffect(() => {
    if (!svgRef.current || !data) return;
    const svg = d3.select(svgRef.current);

    // Simple text wrapping using canvas measurement
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    function measure(text, fontSize, fontFamily, fontWeight) {
      ctx.font = `${fontWeight || '600'} ${fontSize}px ${fontFamily}`;
      return ctx.measureText(text).width;
    }
    function wrap(str, maxWidth, fontSize, fontFamily, fontWeight) {
      if (!str) return [];
      const tokens = str.split(/(\s+|(?=[\/&]))/g).filter(t => t.trim());
      const lines = [];
      let cur = '';
      for (const tok of tokens) {
        const cand = cur ? (cur + ' ' + tok) : tok;
        if (measure(cand, fontSize, fontFamily, fontWeight) > maxWidth && cur) {
          lines.push(cur); cur = tok;
        } else { cur = cand; }
      }
      if (cur) lines.push(cur);
      return lines;
    }

    svg.selectAll('.node-group').each(function(d) {
      const group = d3.select(this);
      group.selectAll('.node-label, .node-vendor-label').remove();

      const swap = labelSwap;
      const primaryText = swap ? (d.product_name || d.vendor_name || d.short) : d.short;
      const secondaryText = swap ? d.short : (d.product_name || d.vendor_name || '');
      const r = d._radius || 24;
      const pFontSz = d.importance === 3 ? 12.5 : d.importance === 2 ? 11 : 10;
      const vFontSz = d.importance === 3 ? 9.5 : 8.5;
      const targetW = d.importance === 3 ? 66 : d.importance === 2 ? 56 : 46;

      const pLines = wrap(primaryText, targetW, pFontSz, FONT_PLATFORM, '600');
      const vLines = secondaryText ? wrap(secondaryText, targetW, vFontSz, FONT_VENDOR, '500') : [];
      const pLH = pFontSz + 2;
      const vLH = vFontSz + 2.5;
      const gap = vLines.length > 0 ? 3 : 0;
      const totalH = pLines.length * pLH + gap + vLines.length * vLH;
      let y = -totalH / 2 + pFontSz * 0.7;

      pLines.forEach(line => {
        group.append('text').attr('class', 'node-label').attr('x', 0).attr('y', y)
          .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
          .attr('font-size', `${pFontSz}px`).attr('font-weight', '600')
          .attr('fill', '#0F172A').attr('font-family', FONT_PLATFORM).text(line);
        y += pLH;
      });
      y += gap;
      vLines.forEach(line => {
        group.append('text').attr('class', 'node-vendor-label').attr('x', 0).attr('y', y)
          .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
          .attr('font-size', `${vFontSz}px`).attr('font-weight', '500')
          .attr('fill', '#64748B').attr('font-family', FONT_VENDOR).text(line);
        y += vLH;
      });
    });
  }, [labelSwap, data]);


  // Apply logo style/opacity changes live + persist
  useEffect(() => {
    localStorage.setItem('lodgiflow_logo_opacity', String(logoOpacity));
    localStorage.setItem('lodgiflow_logo_style', logoStyle);
    logoOpacityRef.current = logoOpacity;
    logoStyleRef.current = logoStyle;
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const inLogoCluster = layoutModeRef.current === 'logo-cluster';
    svg.selectAll('.vendor-logo-bg')
      .attr('opacity', logoOpacity)
      .style('display', !inLogoCluster && logoStyle === 'watermark' ? null : 'none');
    svg.selectAll('.vendor-logo-badge')
      .style('display', !inLogoCluster && logoStyle === 'badge' ? null : 'none');
    svg.selectAll('.vendor-logo-badge image')
      .attr('opacity', Math.max(logoOpacity, 0.15));
  }, [logoOpacity, logoStyle]);

  // Update visual states for focus/search/selection/compare
  useEffect(() => {
    if (!svgRef.current || !data) return;
    const svg = d3.select(svgRef.current);
    const searchLower = (searchQuery || '').toLowerCase();
    const comparison = compareMode && compareData?.comparison ? compareData.comparison : null;

    // Toggle node shapes based on layout mode
    const useRects = layoutMode === 'flowchart' || layoutMode === 'grid' || layoutMode === 'orgchart' || layoutMode === 'clean2';
    const isClean = layoutMode === 'clean1' || layoutMode === 'clean2';
    const isSwim = layoutMode === 'swim-cat' || layoutMode === 'swim-prop';
    const isLogoCluster = layoutMode === 'logo-cluster';
    const isLogoClusterSoft = layoutMode === 'logo-cluster-soft';
    const anyLogoCluster = isLogoCluster || isLogoClusterSoft;
    svg.selectAll('.node-circle').style('display', useRects ? 'none' : null);
    svg.selectAll('.node-rect').style('display', useRects ? null : 'none');
    // Also hide stack circles in rect mode
    svg.selectAll('.stack-circle').style('display', useRects ? 'none' : null);
    // Logo Cluster mode: swap default labels/logos for the lf elements
    svg.selectAll('.lf-group').style('display', anyLogoCluster ? null : 'none');
    // Soft variant: hide the ring/backdrop around the logo badge so it floats
    svg.selectAll('.lf-badge-ring').style('display', isLogoClusterSoft ? 'none' : null);
    svg.selectAll('.node-label').style('display', anyLogoCluster ? 'none' : null);
    svg.selectAll('.node-vendor-label').style('display', anyLogoCluster ? 'none' : null);
    svg.selectAll('.vendor-logo-bg').style('display', anyLogoCluster ? 'none' : (logoStyleRef.current === 'watermark' ? null : 'none'));
    svg.selectAll('.vendor-logo-badge').style('display', anyLogoCluster ? 'none' : (logoStyleRef.current === 'badge' ? null : 'none'));
    // Clean up swimlane backgrounds when not in swimlane mode
    if (!isSwim) svg.selectAll('.swimlane-bg').remove();

    // Clean mode pastel palette
    const PASTEL_MAP = {
      'core-ops': '#B8D4E3', 'rev-dist': '#B5CDA3', 'sales-events': '#C4B5D9',
      'marketing': '#A8C8E8', 'guest-exp': '#E8D4B8', 'fb-tech': '#D4C4A8',
      'finance-hr': '#A8D4B8', 'ancillary': '#E0B8C4', 'it-infra': '#B8C4D4',
      'collab': '#A8D8E0', 'Finance': '#B8D4C8',
    };
    if (isClean) {
      svg.selectAll('.node-circle')
        .attr('fill', d => PASTEL_MAP[d.category_id] || '#D4DCE8')
        .attr('stroke', d => { const c = d3.color(PASTEL_MAP[d.category_id] || '#B8C4D4'); return c ? c.darker(0.4) : '#8899AA'; })
        .attr('stroke-width', 1.5);
      svg.selectAll('.node-rect')
        .attr('fill', d => PASTEL_MAP[d.category_id] || '#D4DCE8')
        .attr('stroke', d => { const c = d3.color(PASTEL_MAP[d.category_id] || '#B8C4D4'); return c ? c.darker(0.3) : '#8899AA'; })
        .attr('stroke-width', 1)
        .attr('rx', 14).attr('ry', 14)
        .attr('filter', 'drop-shadow(0 2px 5px rgba(15,23,42,0.10))');
      svg.selectAll('.link-line').attr('stroke-opacity', 0.35).attr('stroke', '#94A3B8');
      svg.selectAll('.node-label').attr('fill', '#475569').style('font-weight', '400');
      svg.selectAll('.vendor-label').attr('fill', '#94A3B8');
    } else if (isLogoClusterSoft) {
      // Soft cluster: no outer stroke, translucent tint of category color, subtle inner shadow
      svg.selectAll('.node-circle')
        .attr('fill', d => {
          const c = d3.color(d.category_color);
          if (!c) return '#F1F5F9';
          c.opacity = d.used ? 0.18 : 0.09;
          return c.toString();
        })
        .attr('stroke', 'none')
        .attr('stroke-width', 0);
      svg.selectAll('.node-rect')
        .attr('fill', d => {
          const c = d3.color(d.category_color);
          if (!c) return '#F1F5F9';
          c.opacity = d.used ? 0.18 : 0.09;
          return c.toString();
        })
        .attr('stroke', 'none')
        .attr('stroke-width', 0)
        .attr('rx', 14).attr('ry', 14);
      svg.selectAll('.node-label').attr('fill', null).style('font-weight', null);
      svg.selectAll('.vendor-label').attr('fill', null);
    } else {
      svg.selectAll('.node-circle')
        .attr('fill', d => d.used ? '#FFFFFF' : '#F8FAFC')
        .attr('stroke', d => d.category_color)
        .attr('stroke-width', d => d.used ? 2 : 1.25);
      svg.selectAll('.node-rect')
        .attr('fill', d => d.used ? '#FFFFFF' : '#F8FAFC')
        .attr('stroke', d => d.category_color)
        .attr('stroke-width', d => d.used ? 1.75 : 1)
        .attr('rx', 14).attr('ry', 14)
        .attr('filter', d => d.used ? 'drop-shadow(0 2px 6px rgba(15,23,42,0.14))' : 'none');
      svg.selectAll('.node-label').attr('fill', null).style('font-weight', null);
      svg.selectAll('.vendor-label').attr('fill', null);
    }

    const matchesSearch = (d) => {
      if (!searchLower) return true;
      return d.name.toLowerCase().includes(searchLower) ||
        d.short.toLowerCase().includes(searchLower) ||
        (d.vendor_name || '').toLowerCase().includes(searchLower) ||
        d.category_name.toLowerCase().includes(searchLower);
    };

    // PMS Hub mode: compute connected nodes
    let pmsConnectedIds = null;
    if (layoutMode === 'pms-hub' && data) {
      pmsConnectedIds = new Set(['pms']);
      const allLinks = [...(data.links || []), ...(data.product_links || [])];
      allLinks.forEach(l => {
        const sid = l.source?.id || l.source;
        const tid = l.target?.id || l.target;
        if (sid === 'pms') pmsConnectedIds.add(tid);
        if (tid === 'pms') pmsConnectedIds.add(sid);
      });
      // Add secondary connections
      const directIds = new Set(pmsConnectedIds);
      allLinks.forEach(l => {
        const sid = l.source?.id || l.source;
        const tid = l.target?.id || l.target;
        if (directIds.has(sid) || directIds.has(tid)) {
          pmsConnectedIds.add(sid);
          pmsConnectedIds.add(tid);
        }
      });
      // Also keep any node that is "used" (has solutions deployed)
      if (selectedProperty && selectedProperty !== 'overall') {
        data.nodes?.forEach(n => { if (n.used) pmsConnectedIds.add(n.id); });
      }
    }

    svg.selectAll('.node-group').each(function(d) {
      const group = d3.select(this);
      let opacity = 1;
      let visible = true;
      const inFocused = !focusedCategory || d.category_id === focusedCategory;
      const isMatch = matchesSearch(d);
      const isSelected = selectedNode?.id === d.id;
      const isEditSource = editSourceNode?.id === d.id;

      // PMS Hub: hide unconnected unused nodes
      if (pmsConnectedIds && !pmsConnectedIds.has(d.id) && !isSelected) {
        visible = false;
        opacity = 0;
      }

      if (visible) {
        if (focusedCategory && !inFocused) opacity = 0.08;
        if (searchLower && !isMatch) opacity = 0.08;
        if (isSelected) opacity = 1;
        if (isEditSource) opacity = 1;
      }

      // Compare mode: dim nodes not in the comparison
      const cmp = comparison?.[d.id];
      if (comparison && !cmp && !isSelected) {
        opacity = 0.12;
      }

      group.transition().duration(200).attr('opacity', opacity);

      // Selected/edit-source ring
      const circle = group.select('.node-circle');
      if (isEditSource) {
        circle
          .attr('stroke-width', 4.5)
          .attr('stroke', '#0EA5E9')
          .attr('stroke-dasharray', 'none')
          .attr('filter', 'drop-shadow(0 0 8px rgba(14, 165, 233, 0.45))');
      } else if (isSelected) {
        circle
          .attr('stroke-width', 4.5)
          .attr('stroke', d.used ? d.category_color : 'rgba(15,23,42,0.32)')
          .attr('stroke-dasharray', 'none')
          .attr('filter', 'none');
      } else if (comparison && cmp) {
        // Compare mode styling
        if (cmp.status === 'match') {
          circle
            .attr('stroke-width', 3.5)
            .attr('stroke', '#6DCCB1')
            .attr('stroke-dasharray', 'none')
            .attr('fill', '#ECFDF5')
            .attr('filter', 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))');
        } else if (cmp.status === 'actual_only') {
          circle
            .attr('stroke-width', 3)
            .attr('stroke', '#F59E0B')
            .attr('stroke-dasharray', 'none')
            .attr('fill', '#FFFBEB')
            .attr('filter', 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))');
        } else if (cmp.status === 'ideal_only') {
          circle
            .attr('stroke-width', 3.5)
            .attr('stroke', '#3B82F6')
            .attr('stroke-dasharray', '6 3')
            .attr('fill', '#EFF6FF')
            .attr('filter', 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))');
        }
      } else {
        circle
          .attr('stroke-width', d.used ? 2.5 : 1.8)
          .attr('stroke', d.used ? d.category_color : 'rgba(15,23,42,0.32)')
          .attr('stroke-dasharray', d.used ? 'none' : '5 3')
          .attr('fill', d.used ? '#FFFFFF' : '#F1F5F9')
          .attr('filter', 'none');
      }

      // Cursor hint in edit mode
      group.style('cursor', editMode ? 'pointer' : 'pointer');

      // Mirror styling to rect (for flow/grid modes)
      const rect = group.select('.node-rect');
      const circleStroke = circle.attr('stroke');
      const circleStrokeWidth = circle.attr('stroke-width');
      const circleFill = circle.attr('fill');
      const circleFilter = circle.attr('filter');
      rect.attr('stroke', circleStroke).attr('stroke-width', circleStrokeWidth)
        .attr('fill', circleFill).attr('filter', circleFilter);
    });

    svg.selectAll('.link-line').each(function(d) {
      const line = d3.select(this);
      let opacity = 1;
      const sid = typeof d.source === 'object' ? d.source.id : d.source;
      const tid = typeof d.target === 'object' ? d.target.id : d.target;

      // PMS Hub: hide links to hidden nodes
      if (pmsConnectedIds && (!pmsConnectedIds.has(sid) || !pmsConnectedIds.has(tid))) {
        opacity = 0;
      }

      if (focusedCategory) {
        const sNode = data.nodes.find(n => n.id === sid);
        const tNode = data.nodes.find(n => n.id === tid);
        if (sNode?.category_id !== focusedCategory && tNode?.category_id !== focusedCategory) {
          opacity = 0.04;
        }
      }
      if (selectedNode) {
        opacity = (sid === selectedNode.id || tid === selectedNode.id) ? 1 : 0.06;
      }
      if (comparison) {
        opacity = 0.15;
      }

      line.transition().duration(200).attr('opacity', opacity);
    });

    // Category labels removed — no opacity updates needed

    // Update line styling when controls change
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.selectAll('.platform-link')
        .attr('stroke-opacity', lineOpacity)
        .attr('stroke-width', d => {
          if (d.integration_status === 'gap') return lineThickness * 0.8;
          return d.direction === 'two-way' ? lineThickness : lineThickness * 0.7;
        });
      svg.selectAll('.product-link')
        .attr('stroke-opacity', Math.min(lineOpacity + 0.2, 1))
        .attr('stroke-width', lineThickness * 1.2);
    }
  }, [focusedCategory, searchQuery, selectedNode, bulkSelectedIds, data, editMode, editSourceNode, compareMode, compareData, layoutMode, selectedProperty, lineOpacity, lineThickness]);

  return (
    <div
      ref={containerRef}
      className="graph-container w-full h-full relative"
      data-testid="graph-canvas"
      style={{
        background: '#F7FAFC',
        cursor: editMode ? 'crosshair' : undefined,
      }}
    >
      <svg
        ref={svgRef}
        className="graph-canvas"
        width={dimensions.width}
        height={dimensions.height}
        style={{ display: 'block' }}
      />
      {mapNeedsRefresh && (
        <button
          onClick={() => { userHasZoomedRef.current = false; fitToView(true); }}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card/90 backdrop-blur border shadow-lg text-xs font-medium text-primary hover:bg-card transition-colors"
          data-testid="fit-to-view-btn"
        >
          Fit to View
        </button>
      )}
      <div ref={tooltipRef} className="graph-tooltip" style={{ display: 'none' }} />
    </div>
  );
});

GraphCanvas.displayName = 'GraphCanvas';
export default GraphCanvas;
